import React, { useEffect, useState } from 'react';
import {
    Grid,
    Card,
    Typography,
    Pagination,
    Button,
    Box,
    Checkbox
} from '@mui/material';
import theme from '../../common/theme';
import { HorizontalLayout, VerticalLayout } from '../../common/common-layout';
import BTSearchBox from '../../common/components/BTSearchBox';
import BTBox from '../../common/components/BTBox';

interface CardData {
    id?: string;
    title: string;
    imageSrc?: string;
    displayDate?: string;   // ✅ Post.postDate를 여기로 매핑해서 전달
}

interface CardListProps {
    data: CardData[];
    itemsPerPage?: number;
    onAddClick?: () => void;
    onDeleteClick?: (selectedIndexes: number[]) => void;
    addButtonText?: string;
    deleteButtonText?: string;
    checkboxSelection?: boolean;
    handleCardClick?: (index: number) => void;
}

const CardList: React.FC<CardListProps> = ({
    data,
    itemsPerPage = 18,
    onAddClick,
    onDeleteClick,
    addButtonText = '추가',
    deleteButtonText = '삭제',
    checkboxSelection = false,
    handleCardClick,
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [filteredData, setFilteredData] = useState<CardData[]>(data);
    const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);
    const [currentSearchKeywords, setCurrentSearchKeywords] = useState<string[]>([]);

    useEffect(() => {
        const newFiltered = data.filter((item) => {
            const text = item.title.toLowerCase();
            return currentSearchKeywords.every(keyword => text.includes(keyword));
        });

        setFilteredData(newFiltered);

        const maxPage = Math.max(1, Math.ceil(newFiltered.length / itemsPerPage));
        setCurrentPage(prevPage => Math.min(prevPage, maxPage));

        const validIndexes = newFiltered.map(item => data.indexOf(item));
        setSelectedIndexes(prev => prev.filter(i => validIndexes.includes(i)));
    }, [data]);

    useEffect(() => {
        // 삭제(또는 추가)로 데이터 길이가 변하면 선택 상태 초기화
        setSelectedIndexes([]);
    }, [data.length]);

    const handleSearch = (query: string) => {
        const keywords = query.toLowerCase().split(' ').filter(word => word.trim() !== '');
        setCurrentSearchKeywords(keywords);

        const filtered = data.filter(item => {
            const text = item.title.toLowerCase();
            return keywords.every(keyword => text.includes(keyword));
        });

        setFilteredData(filtered);
        setCurrentPage(1);

        const filteredIndexes = filtered.map(item => data.indexOf(item));
        setSelectedIndexes(prev => prev.filter(index => filteredIndexes.includes(index)));
    };

    const handleCheckboxChange = (index: number) => {
        setSelectedIndexes(prev =>
            prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
        );
    };

    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const paginatedData = filteredData.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    return (
        <>
            <Box width="100%" display="flex" justifyContent="center">
                <VerticalLayout width='100%'>
                    {/* ⭐ BTList와 동일한 상단 툴바 레이아웃 */}
                    <Box
                        width="100%"
                        height="50px"
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ minWidth: 0, boxSizing: 'border-box', pr: 1 }}
                    >
                        <Box
                            flex={1}
                            display="flex"
                            alignItems="center"
                            sx={{ minWidth: 0, gap: 2, flexWrap: 'wrap', boxSizing: 'border-box' }}
                        />
                        <Box width="300px">
                            <BTSearchBox onSearch={handleSearch} />
                        </Box>
                    </Box>

                    <BTBox height='16px' />

                    {filteredData.length === 0 ? (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            minHeight="200px"
                            width="100%"
                            fontSize="18px"
                            color="#888"
                        >
                            데이터가 없습니다.
                        </Box>
                    ) : (
                        <Grid container spacing={2} sx={{ width: "100%", boxSizing: "border-box" }}>
                            {paginatedData.map((item, index) => {
                                const actualIndex = data.indexOf(item);

                                return (
                                    <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={item.id}>
                                        <Card
                                            onClick={() => handleCardClick?.(actualIndex)}
                                            sx={{
                                                width: "100%",
                                                display: 'flex',
                                                flexDirection: 'column',
                                                boxShadow: "none",
                                                border: "1px solid #ddd"
                                            }}
                                        >
                                            {/* 이미지 영역 (BTImageList 스타일) */}
                                            <Box
                                                position="relative"
                                                width="100%"
                                                sx={{ aspectRatio: '1 / 1', backgroundColor: '#f5f5f5' }}
                                            >
                                                {item.imageSrc ? (
                                                    <img
                                                        src={item.imageSrc}
                                                        alt={item.title}
                                                        style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'contain'
                                                        }}
                                                    />
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: '50%',
                                                            left: '50%',
                                                            transform: 'translate(-50%, -50%)'
                                                        }}
                                                    >
                                                        <Typography color="#999" fontSize="20px">
                                                            No Image
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {checkboxSelection && (
                                                    <Box position="absolute" top={4} right={4} zIndex={1}>
                                                        <Checkbox
                                                            checked={selectedIndexes.includes(actualIndex)}
                                                            onChange={() => handleCheckboxChange(actualIndex)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            sx={{
                                                                color: '#14B446 !important',
                                                                p: 0.5,
                                                                '& .MuiSvgIcon-root': { fontSize: 24 },
                                                                borderRadius: '4px',
                                                            }}
                                                        />
                                                    </Box>
                                                )}
                                            </Box>

                                            {/* 텍스트 영역 (BTImageList 스타일) */}
                                            <Box p={1} display="flex" flexDirection="column" alignItems="flex-start">
                                                <Typography
                                                    fontSize="16px"
                                                    fontWeight={600}
                                                    whiteSpace="pre-wrap"
                                                    overflow="hidden"
                                                    textOverflow="ellipsis"
                                                    sx={{
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        height: '48px',
                                                    }}
                                                >
                                                    {item.title}
                                                </Typography>
                                                <Box width="100%" display="flex" justifyContent="space-between">
                                                    <Typography fontSize="13px" color="#666">
                                                        {item.displayDate || '-'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </VerticalLayout>
            </Box>

            {data.length > 0 && (
                <Box width="100%" display="flex" justifyContent="center" mt={2} pb={2} sx={{ flexShrink: 0 }}>
                    <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={(e, value) => setCurrentPage(value)}
                        color="primary"
                    />
                </Box>
            )}

            <Box width="100%" display="flex" justifyContent="space-between" mt={3} sx={{ flexShrink: 0, pt: 1 }}>
                {onDeleteClick && (
                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: '#FC5A5A',
                            color: 'white',
                            fontSize: '18px',
                            fontWeight: '700',
                            boxShadow: 'none',
                            '&:hover': { backgroundColor: theme.colors.colorWarn },
                            width: '120px',
                            height: '48px',
                            '&:focus': { outline: 'none' },
                            '&:active': { outline: 'none' },
                        }}
                        onClick={() => onDeleteClick(selectedIndexes)}
                    >
                        {deleteButtonText}
                    </Button>
                )}
                {onAddClick && (
                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: theme.colors.bbTech,
                            color: 'white',
                            fontSize: '18px',
                            fontWeight: '700',
                            boxShadow: 'none',
                            '&:hover': { backgroundColor: theme.colors.bbTech },
                            width: '120px',
                            height: '48px',
                            '&:focus': { outline: 'none' },
                            '&:active': { outline: 'none' },
                        }}
                        onClick={onAddClick}
                    >
                        {addButtonText}
                    </Button>
                )}
            </Box>
        </>
    );
};

export default CardList;